"use client";

import { useEffect, useState } from "react";

/**
 * The typewriter placeholder overlay — types an example, holds, deletes, and
 * moves to the next, with a blinking caret.
 *
 * Extracted from `HeroSearchInput` 2026-09-06 when the docked mini assistant
 * needed the same behaviour. It was already written to be self-contained for a
 * performance reason worth preserving, so keep these two rules:
 *
 * ⛔ IT MUST OWN ITS OWN STATE. This is a ~20-render-per-second loop; a child's
 * `setState` never re-renders its parent, so keeping `charCount` here is what
 * stops every tick reconciling the whole host component (and, on the homepage,
 * its always-mounted suggestion dropdown). Do NOT lift this state — that was
 * the pre-extraction bug in the hero.
 *
 * ⚠️ It reads reduced motion from the CALLER rather than the hook, because the
 * host already needs that answer for its own transitions and the two must not
 * disagree. Under reduced motion it renders the first example statically, with
 * no timers and no caret — the global CSS `prefers-reduced-motion` override
 * only covers `animation-*`, so a JS-driven loop is not covered by it.
 *
 * The host is responsible for: gating on a mounted flag (so SSR renders the
 * static placeholder and there is no hydration flash), blanking the field's
 * native `placeholder` while this is shown, and positioning via `className`.
 */

const TYPE_MS = 55;    // per-character typing speed
const DELETE_MS = 28;  // per-character delete speed — snappier than typing
const HOLD_MS = 1500;  // dwell on the fully-typed phrase before deleting
const SWITCH_MS = 420; // pause on empty before typing the next phrase

export default function AnimatedPlaceholder({
  examples,
  reducedMotion,
  className,
  wrap = false,
}: {
  examples: string[];
  reducedMotion: boolean;
  /** Positioning + type styles. The overlay sits over the host's own field, so
   *  only the host knows where that is. Must leave room for any trailing
   *  controls, and needs `min-w-0` on the text for truncation to work. */
  className: string;
  /** Let a long example WRAP instead of ending in an ellipsis (owner,
   *  2026-09-18), for a host whose field wraps too — the homepage hero. The
   *  caret then moves inside the text so it follows the last character onto
   *  the new line instead of sitting at the block's right edge. ⛔ Not for a
   *  single-line host: the docked pill is a 30px control and a second line
   *  would paint straight through it. */
  wrap?: boolean;
}) {
  const [exampleIndex, setExampleIndex] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [deleting, setDeleting] = useState(false);

  // Typewriter step machine. Each render schedules the next step (type a char,
  // hold, delete a char, or advance to the next phrase) via one timer; the
  // displayed substring is derived from charCount. Skipped under reduced-motion.
  useEffect(() => {
    if (reducedMotion) return;
    const full = examples[exampleIndex];
    let delay: number;
    let step: () => void;
    if (!deleting && charCount < full.length) {
      delay = TYPE_MS;
      step = () => setCharCount((c) => c + 1);
    } else if (!deleting && charCount === full.length) {
      delay = HOLD_MS;
      step = () => setDeleting(true);
    } else if (deleting && charCount > 0) {
      delay = DELETE_MS;
      step = () => setCharCount((c) => c - 1);
    } else {
      delay = SWITCH_MS;
      step = () => {
        setDeleting(false);
        setExampleIndex((i) => (i + 1) % examples.length);
      };
    }
    const timer = setTimeout(step, delay);
    return () => clearTimeout(timer);
  }, [reducedMotion, examples, exampleIndex, charCount, deleting]);

  const text = reducedMotion ? examples[0] : examples[exampleIndex].slice(0, charCount);

  const caret = !reducedMotion && (
    <span
      className="ml-1 inline-block h-[1.05em] w-[1.5px] shrink-0 bg-gray-500"
      style={{ animation: "caret-blink 1.1s step-end infinite" }}
    />
  );

  return (
    <span aria-hidden="true" className={className}>
      <span className={wrap ? "min-w-0" : "min-w-0 truncate"}>
        {text}
        {wrap && caret}
      </span>
      {!wrap && caret}
    </span>
  );
}
